import { CurrencyPipe } from '@angular/common'
import { Component, inject, input, output } from '@angular/core'
import { toObservable, toSignal } from '@angular/core/rxjs-interop'

import { TranslatePipe } from '@ngx-translate/core'
import { subDays } from 'date-fns'
import { distinctUntilChanged, switchMap } from 'rxjs'
import { map } from 'rxjs/operators'

import { OrganizationService } from '../../../../services/organization/organization.service'
import { ProductsService } from '../../../../services/product/products.service'
import { SubscriptionService } from '../../../../services/subscription/subscription.service'
import { LocalizedDatePipe } from '../../../../shared/pipes/localized-date.pipe'
import type { OrderLine } from '../../models/order-line'

@Component({
  selector: 'dhp-product-checkout-list',
  templateUrl: './product-checkout-list.component.html',
  styleUrl: './product-checkout-list.component.scss',
  imports: [CurrencyPipe, TranslatePipe, LocalizedDatePipe],
})
export class ProductCheckoutListComponent {
  //#region Input properties
  orderLines = input.required<OrderLine[]>()
  hasPendingScheduledAdjustments = input<boolean>(false)
  isCheckoutDisabled = input<boolean>(false)
  isProrated = input<boolean>(false)
  organizationId = input.required<number | null>()
  subscriptionStartDate = input<Date | null>(null)
  subscriptionEndDate = input<Date | null>(null)
  submitLabelKey = input<string>('pages.checkout.overview.submit')
  //#endregion Input properties

  //#region Services
  private readonly productService = inject(ProductsService)
  private readonly subscriptionService = inject(SubscriptionService)
  private readonly organizationService = inject(OrganizationService)
  //#endregion Services

  //#region Emitters
  // Emitter for confirming the order
  confirm = output()
  //#endregion Emitters

  //#region Signals
  // Signal to determine if the organization is exempt from VAT
  readonly isExemptForVat = toSignal(
    this.organizationService
      .getOrganization()
      .pipe(map(org => !!org?.tax_exemption || !!org?.tax_reverse_charge)),
    { initialValue: false }
  )

  // Signal to get the proration factor from the subscription service
  private readonly prorationFactor = toSignal(
    toObservable(this.organizationId).pipe(
      distinctUntilChanged(),
      switchMap(id =>
        this.subscriptionService.getProrationFactor(id ?? undefined)
      )
    ),
    { initialValue: null }
  )
  //#endregion Signals

  //#region Public methods
  /**
   * Calculate subtotal (net), prorated if applicable
   *
   * @returns {number} Subtotal amount
   */
  getSubtotal(): number {
    // Sum up prices for all order lines
    return this.orderLines().reduce((acc, cur) => {
      // Calculate price for each order line and accumulate
      return this.getPrice(cur) + acc
    }, 0)
  }

  getVisualSubscriptionEndDate(): Date | null {
    const subscriptionEndDate = this.subscriptionEndDate()
    if (!subscriptionEndDate) return null

    return subDays(subscriptionEndDate, 1)
  }

  hasPositiveOrderLines(): boolean {
    return this.orderLines().some(orderLine => orderLine.seats > 0)
  }

  canConfirm(): boolean {
    return (
      !this.isCheckoutDisabled() &&
      (this.hasPositiveOrderLines() || this.hasPendingScheduledAdjustments())
    )
  }

  getAutoIncludedSummaryKey(orderLine: OrderLine): string {
    switch (orderLine.autoIncludedUpgradeType) {
      case 'adjustScheduledDecrease':
        return 'pages.checkout.info.autoIncludedUpgradeAdjustScheduled'
      case 'removeScheduledDecrease':
        return 'pages.checkout.info.autoIncludedUpgradeRemoveScheduled'
      case 'increaseCurrentSeats':
      default:
        return 'pages.checkout.info.autoIncludedUpgradeIncrease'
    }
  }

  getAutoIncludedSummaryParams(orderLine: OrderLine): {
    current?: number
    from?: number
    to: number
  } {
    switch (orderLine.autoIncludedUpgradeType) {
      case 'adjustScheduledDecrease':
      case 'removeScheduledDecrease':
        return {
          current: orderLine.existingSeats,
          from: orderLine.scheduledSeats,
          to: orderLine.seats,
        }
      case 'increaseCurrentSeats':
      default:
        return {
          from: orderLine.currentSeats,
          to: orderLine.seats,
        }
    }
  }

  /**
   * Get price for an order line, prorated if applicable.
   *
   * @param orderLine - Checkout order line
   * @returns {number} Price amount
   */
  getPrice(orderLine: OrderLine): number {
    if (
      orderLine.autoIncludedUpgradeType === 'adjustScheduledDecrease' ||
      orderLine.autoIncludedUpgradeType === 'removeScheduledDecrease'
    ) {
      return 0
    }

    // Get base price
    const basePrice = this.productService.getPrice(
      orderLine.product.product_options.docs,
      orderLine.seats
    )

    if (orderLine.currentSeats !== undefined) {
      const currentPrice = this.productService.getPrice(
        orderLine.product.product_options.docs,
        orderLine.currentSeats
      )

      return (basePrice - currentPrice) * this.getProrationFactor()
    }

    // Return prorated price if applicable else base price times 1
    return basePrice * this.getProrationFactor()
  }

  /**
   * Calculate total with VAT, prorated if applicable
   *
   * @returns {number} Total amount VAT
   */
  getTotal(): number {
    // Sum up prices with VAT for all order lines
    return this.orderLines().reduce((acc, cur) => {
      // Calculate price with VAT for each order line and accumulate
      return this.getPriceVat(cur) + acc
    }, 0)
  }

  /**
   * Get price with VAT for an order line, prorated if applicable.
   *
   * @param orderLine - Checkout order line
   * @returns {number} Price amount with VAT
   */
  getPriceVat(orderLine: OrderLine): number {
    if (
      orderLine.autoIncludedUpgradeType === 'adjustScheduledDecrease' ||
      orderLine.autoIncludedUpgradeType === 'removeScheduledDecrease'
    ) {
      return 0
    }

    // Get base price with VAT
    const basePrice = this.productService.getPriceVat(
      orderLine.product.product_options.docs,
      orderLine.seats
    )

    if (orderLine.currentSeats !== undefined) {
      const currentPrice = this.productService.getPriceVat(
        orderLine.product.product_options.docs,
        orderLine.currentSeats
      )

      return (basePrice - currentPrice) * this.getProrationFactor()
    }

    // Return prorated price with VAT if applicable else base price times 1
    return basePrice * this.getProrationFactor()
  }

  /**
   * Returns a breakdown of VAT by all present rates, with label and amount
   *
   * @returns {{ rate: number; amount: number }[]} VAT options array
   */
  getVatOptions(): { rate: number; amount: number }[] {
    const vatAmounts: Record<string, number> = {}
    const proration = this.getProrationFactor()
    for (const orderLine of this.orderLines()) {
      if (
        orderLine.autoIncludedUpgradeType === 'adjustScheduledDecrease' ||
        orderLine.autoIncludedUpgradeType === 'removeScheduledDecrease'
      ) {
        continue
      }

      const options = this.productService.getProductOptionsForSeats(
        orderLine.product.product_options.docs,
        orderLine.seats
      )
      const currentOptions =
        orderLine.currentSeats === undefined
          ? []
          : this.productService.getProductOptionsForSeats(
              orderLine.product.product_options.docs,
              orderLine.currentSeats
            )

      for (const option of options) {
        const price = option.productOption.invoicing.price
        const taxRate = Number(option.productOption.invoicing.tax_rate)
        const quantity = option.quantity
        const net = price * quantity * proration
        const vat = net * (taxRate / 100)
        if (!Object.keys(vatAmounts).includes(String(taxRate))) {
          vatAmounts[taxRate] = 0
        }
        vatAmounts[taxRate] += vat
      }

      for (const option of currentOptions) {
        const price = option.productOption.invoicing.price
        const taxRate = Number(option.productOption.invoicing.tax_rate)
        const quantity = option.quantity
        const net = price * quantity * proration
        const vat = net * (taxRate / 100)
        if (!Object.keys(vatAmounts).includes(String(taxRate))) {
          vatAmounts[taxRate] = 0
        }
        vatAmounts[taxRate] -= vat
      }
    }
    // Return as an array with label, sorted descending by rate
    return Object.entries(vatAmounts)
      .map(([rate, amount]) => ({
        rate: Number(rate),
        amount,
      }))
      .sort((a, b) => b.rate - a.rate)
  }
  //#endregion Public methods

  //#region Private methods
  /**
   * Get proration factor, or 1 if not prorating
   *
   * @private
   * @returns {number} Proration factor
   */
  private getProrationFactor(): number {
    // Return 1 if not prorating
    if (!this.isProrated()) return 1

    // Return 0 until proration factor is loaded
    const prorationFactor = this.prorationFactor()
    if (prorationFactor === null) return 0

    // Return proration factor
    return prorationFactor
  }
  //#endregion Private methods
}
